// NodePeekContext.js

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { UNIFIED_TAB_KEYS } from 'node/tabs/unifiedNodeTabKeys';

const NodePeekContext = createContext(null);
const PEEK_HISTORY_LIMIT = 30;
const DEFAULT_PEEK_PANEL_TAB_KEY = UNIFIED_TAB_KEYS.ABOUT;

export const NodePeekProvider = ({ children }) => {
  // ----- STATE ----- //

  // Active Peek Node Key
  const [peekNodeKey, setPeekNodeKey] = useState('');

  // Seed Node Snapshot
  const [peekSeedNode, setPeekSeedNode] = useState(null);

  // Peek Open State
  const [isPeekOpen, setIsPeekOpen] = useState(false);

  // Active Peek Panel Tab
  const [peekPanelTabKey, setPeekPanelTabKey] = useState(DEFAULT_PEEK_PANEL_TAB_KEY);

  // Peek Back Stack
  const [peekHistoryStack, setPeekHistoryStack] = useState([]);

  // ----- HANDLERS ----- //

  const openPeek = useCallback((node, options = {}) => {
    const key = typeof node?.key === 'string' ? node.key.trim() : '';
    if (!key) return;

    const mode = options?.mode === 'push' ? 'push' : 'reset';

    setPeekHistoryStack((prev) => {
      if (mode !== 'push') return [];

      const currentKey = typeof peekNodeKey === 'string' ? peekNodeKey.trim() : '';
      if (!currentKey || currentKey === key) return prev;

      const currentEntry = {
        key: currentKey,
        seedNode: peekSeedNode?.key === currentKey ? peekSeedNode : null,
      };

      const lastEntry = prev[prev.length - 1] || null;
      if (lastEntry?.key === currentEntry.key) return prev;

      const next = [...prev, currentEntry];
      if (next.length <= PEEK_HISTORY_LIMIT) return next;
      return next.slice(next.length - PEEK_HISTORY_LIMIT);
    });

    setPeekSeedNode(node || null);
    setPeekNodeKey(key);
    setPeekPanelTabKey(DEFAULT_PEEK_PANEL_TAB_KEY);
    setIsPeekOpen(true);
  }, [peekNodeKey, peekSeedNode]);

  const goBackPeek = useCallback(() => {
    let previousEntry = null;

    setPeekHistoryStack((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      previousEntry = prev[prev.length - 1] || null;
      return prev.slice(0, -1);
    });

    const key = typeof previousEntry?.key === 'string' ? previousEntry.key.trim() : '';
    if (!key) return;

    setPeekSeedNode(previousEntry?.seedNode || null);
    setPeekNodeKey(key);
    setPeekPanelTabKey(DEFAULT_PEEK_PANEL_TAB_KEY);
    setIsPeekOpen(true);
  }, []);

  const closePeek = useCallback(() => {
    setPeekPanelTabKey(DEFAULT_PEEK_PANEL_TAB_KEY);
    setIsPeekOpen(false);
  }, []);

  const clearPeek = useCallback(() => {
    setPeekHistoryStack([]);
    setPeekSeedNode(null);
    setPeekNodeKey('');
    setPeekPanelTabKey(DEFAULT_PEEK_PANEL_TAB_KEY);
    setIsPeekOpen(false);
  }, []);

  // ----- MEMOS ----- //

  const value = useMemo(() => ({
    isPeekOpen,
    peekNodeKey,
    peekSeedNode,
    peekPanelTabKey,
    canPeekGoBack: peekHistoryStack.length > 0,
    openPeek,
    goBackPeek,
    closePeek,
    clearPeek,
    setPeekPanelTabKey,
  }), [
    isPeekOpen,
    peekNodeKey,
    peekSeedNode,
    peekPanelTabKey,
    peekHistoryStack.length,
    openPeek,
    goBackPeek,
    closePeek,
    clearPeek,
    setPeekPanelTabKey,
  ]);

  return (
    <NodePeekContext.Provider value={value}>
      {children}
    </NodePeekContext.Provider>
  );
};

export const useNodePeek = () => {
  const context = useContext(NodePeekContext);
  if (!context) {
    throw new Error('useNodePeek must be used within NodePeekProvider');
  }
  return context;
};

export { UNIFIED_TAB_KEYS };

export default NodePeekContext;